import { useState, useEffect } from "react"
import PrimaryModal from "../../UI/Modal/PrimaryModal"
import { PrimaryButton } from "../../UI";
import Loader from "../../UI/Loader/Loader"
import { useContentfulContext } from "../../../contexts/ContentfulContext"


export default function MappingModal({ defaultMapping, onClose, onSave, fields, loading }) {
    const { content } = useContentfulContext()
    const [mappingName, setMappingName] = useState("")
    const [mappings, setMappings] = useState([])
    const [error, setError] = useState("")

    useEffect(() => {
        if (defaultMapping) {
            setMappingName(defaultMapping?.name)
            setMappings(defaultMapping?.mappings)
        } else {
            // Initialize mappings with default values (input = output)
            if (fields.length > 0) {
                const initialMappings = fields.map((field) => ({
                    input_field: field,
                    output_field: field,
                }))
                setMappings(initialMappings)
            }
        }
    }, [fields, defaultMapping])

    const handleOutputFieldChange = (index, value) => {
        const updatedMappings = [...mappings]
        updatedMappings[index].output_field = value
        setMappings(updatedMappings)
    }

    const handleSave = () => {
        if (defaultMapping) return;
        // Validate mapping name
        if (!mappingName.trim()) {
            setError("Mapping name is required");
            return;
        }

        const outputFields = mappings.map((m) => m.output_field?.trim());

        // Check for missing or empty output fields
        if (outputFields.some((field) => !field)) {
            setError("All output fields must be non-empty");
            return;
        }

        // Check for output fields containing spaces
        if (outputFields.some((field) => field.includes(" "))) {
            setError("Output fields must not contain spaces");
            return;
        }

        // Check for special characters
        const invalidCharsRegex = /[^a-zA-Z0-9_]/;
        if (outputFields.some((field) => invalidCharsRegex.test(field))) {
            setError("Output fields must only contain letters, numbers, or underscores (no special characters)");
            return;
        }

        const uniqueFields = new Set(outputFields);
        if (uniqueFields.size !== outputFields.length) {
            setError("Output fields must be unique");
            return;
        }

        // Ensure required fields exist
        if (!uniqueFields.has("id")) {
            setError("Output fields must include 'id' field");
            return;
        }

        if (!uniqueFields.has("name")) {
            setError("Output fields must include 'name' field");
            return;
        }

        onSave(mappingName, mappings);
        setMappingName("");
        setError("");
    };

    return (
        <PrimaryModal title={content?.MANAGE_RECOMMENDATION?.actions?.create_mapping} hideModal={onClose} widthClass="max-w-3xl">
            {loading && <Loader />}
            <div className="p-6 space-y-4">
                <div className="space-y-2">
                    <label htmlFor="mapping-name" className="text-sm font-medium">
                        {content?.MANAGE_RECOMMENDATION?.labels?.mapping_name}
                    </label>
                    <input
                        id="mapping-name"
                        type="text"
                        disabled={defaultMapping ? true : false}
                        value={mappingName}
                        onChange={(e) => setMappingName(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-2"
                        placeholder="Enter mapping name"
                    />
                </div>

                <div className="space-y-2">
                    <h3 className="font-medium">{content?.MANAGE_RECOMMENDATION?.heading?.field_mappings}</h3>
                    <div className="border rounded-md overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">{content?.MANAGE_RECOMMENDATION?.heading?.input_field}</th>
                                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">{content?.MANAGE_RECOMMENDATION?.heading?.output_field}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {mappings.map((mapping, index) => (
                                    <tr key={index} className="bg-white">
                                        <td className="px-4 py-2 text-sm text-gray-900">{mapping.input_field}</td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="text"
                                                disabled={defaultMapping ? true : false}
                                                value={mapping.output_field}
                                                onChange={(e) => handleOutputFieldChange(index, e.target.value)}
                                                className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-2"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        {content?.MANAGE_RECOMMENDATION?.actions?.cancel}
                    </button>
                    {!defaultMapping && <PrimaryButton handleClick={handleSave}>{content?.MANAGE_RECOMMENDATION?.actions?.save_mapping}</PrimaryButton>}
                </div>
            </div>
        </PrimaryModal>
    )
}
